import { useMemo } from 'react'

import { matchFilterString } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { TeamMemberModel, TeamMemberRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'
import { EntitiesState } from '@redux/entities'

const watchEntityKeys = ['teamMembers']

const roleOrder = {
  lead_agent: 4, associate_agent: 3, office_support: 2, remote_support: 1,
}

const sortTeamMembers = (teamMembers: TeamMemberModel[], users: EntitiesState['users']) => (
  teamMembers.sort((teamMemberA, teamMemberB) => {
    // @ts-expect-error TS7053
    const sortARole = roleOrder[teamMemberA.member_role] || teamMemberA.team_lead
    // @ts-expect-error TS7053
    const sortBRole = roleOrder[teamMemberB.member_role] || teamMemberB.team_lead

    const sortAName = users[teamMemberA.user_id]?.full_name || ''
    const sortBName = users[teamMemberB.user_id]?.full_name || ''

    if (sortARole > sortBRole) return -1
    if (sortARole < sortBRole) return 1
    if (sortAName > sortBName) return 1
    if (sortAName < sortBName) return -1

    return 0
  })
)

type TeamMemberFilters = {
  string?: string,
  team_id?: number,
}

type UseTeamMembersOptions = {
  filters?: TeamMemberFilters,
  performHttpRequests?: boolean,
  requestOptions?: TeamMemberRequestOptions,
}

function useTeamMembers(options: UseTeamMembersOptions) {
  const { filters = {}, requestOptions } = options

  const {
    string: filterString,
    team_id: filterTeamId,
  } = filters

  const {
    updatedEntities: { teamMembers: teamMembersUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { teamMembers, users } = useSelector(reduxState => reduxState.entities)

  const filteredTeamMembers = useMemo(() => {
    const filtered = Object.values(teamMembers).filter((teamMember: TeamMemberModel) => {
      const { team_id, user_id } = teamMember
      const user = users[user_id] || {}

      const matchString = matchFilterString(user.full_name, filterString)
      const matchTeam = filterTeamId ? team_id === filterTeamId : true

      return matchString && matchTeam
    })

    return sortTeamMembers(filtered, users)
  }, [teamMembersUpdatedAt, JSON.stringify(filters)])

  const filteredTeamMembersCount = filteredTeamMembers.length
  const hasFilteredTeamMembers = !!filteredTeamMembersCount

  const filteredTeamMemberUserIds = filteredTeamMembers.map(teamMember => teamMember.user_id)

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredTeamMembersCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingTeamMembers } = useReduxAction(
    'teamMembers',
    'loadTeamMembers',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredTeamMemberUserIds,
    filteredTeamMembers,
    filteredTeamMembersCount,
    hasFilteredTeamMembers,
    loading: loadingTeamMembers,
  }
}

export default useTeamMembers
